import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formattedDate } from "../../../components/formatted/FormattedDateAndTime";
import { getCommentsData } from "../../../components/utils/Comments";
import { ANALYSIS_COMMENTS_MAX_DAYS } from "../campaignFormatUtils";

export const useCampaignComments = (
  campaignPeriods,
  selectedCampaign,
  i18n,
  enabled = true,
) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [periodName, setPeriodName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchComments = async () => {
      if (!enabled) {
        return;
      }

      if (campaignPeriods.length === 0) {
        setComments([]);
        setPeriodName(null);
        setIsLoading(false);
        return;
      }

      if (selectedCampaign < 0 || selectedCampaign >= campaignPeriods.length) {
        setComments([]);
        setPeriodName(null);
        setIsLoading(false);
        return;
      }

      if (!campaignPeriods[selectedCampaign]) {
        return;
      }

      setIsLoading(true);
      try {
        const campaign = campaignPeriods[selectedCampaign];
        const periodString = `${t("from")} ${formattedDate(
          campaign.start,
          t,
          true,
          false,
          true,
        )} ${t("au")} ${formattedDate(campaign.stop, t, true, false, true)}`;

        const campaignStart = new Date(campaign.start);
        const campaignStop = new Date(campaign.stop);
        const now = new Date();
        // For ongoing campaigns, use "now" as the end of the comments window.
        // Using campaign.stop (e.g. 31/12/2026) would fetch a future empty range.
        const effectiveStop = new Date(
          Math.min(campaignStop.getTime(), now.getTime()),
        );
        const maxSpanMs = ANALYSIS_COMMENTS_MAX_DAYS * 24 * 60 * 60 * 1000;
        let commentsStart = campaignStart;
        if (effectiveStop.getTime() - campaignStart.getTime() > maxSpanMs) {
          commentsStart = new Date(
            Math.max(
              campaignStart.getTime(),
              effectiveStop.getTime() - maxSpanMs,
            ),
          );
        }

        const response = await getCommentsData(
          commentsStart.toISOString(),
          effectiveStop.toISOString(),
          [],
          campaign.containerObjectIdList,
        );
        setPeriodName(periodString);
        setComments(response);
      } catch (error) {
        console.error("Error fetching comments:", error);
        setComments([]);
        setPeriodName(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchComments();
    // eslint-disable-next-line
  }, [campaignPeriods, selectedCampaign, i18n.language, enabled]);

  return {
    comments,
    periodName,
    isLoading,
  };
};
